import type { Ctx } from '@milkdown/kit/ctx';
import type { useNodeViewFactory } from '@prosemirror-adapter/react';
import { useNodeViewContext } from '@prosemirror-adapter/react';
import { prosePluginsCtx } from '@milkdown/kit/core';
import { Plugin } from '@milkdown/kit/prose/state';
import { DownloadSimple } from '@phosphor-icons/react';
import clsx from 'clsx';

import { ATTACHMENT_NODE_NAME } from '../../plugins/hocuspocus-platform-schema/attachment';
import { FileExtensionBadge } from './components/FileExtensionBadge';
import { formatAttachmentSize } from './utils';

export const AttachmentNodeView = () => {
  const { node, selected, view } = useNodeViewContext();

  const href = node.attrs.href as string | null;
  const title = (node.attrs.title as string) || '';
  const size = Number.parseInt(String(node.attrs.size ?? '0'), 10) || 0;
  const isUploading = !href;

  return (
    <a
      className={clsx(
        'component-attachment widget group my-2 flex w-fit max-w-full items-center gap-1.5 overflow-hidden text-ellipsis whitespace-nowrap rounded-lg border border-border bg-surface px-2 py-1.5 text-default no-underline',
        selected && 'ProseMirror-selectednode',
        href && 'hover:bg-muted/50',
      )}
      href={href ?? undefined}
      target={href ? '_blank' : undefined}
      rel={href ? 'noopener noreferrer nofollow' : undefined}
      contentEditable={false}
      onMouseDown={(event) => {
        if (view.editable && view.hasFocus()) {
          event.preventDefault();
        }
      }}
    >
      <FileExtensionBadge title={title} />
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <strong className="truncate text-sm font-bold">{title}</strong>
        <span className="shrink-0 text-sm leading-none text-muted-foreground">
          {isUploading ? 'Uploading…' : formatAttachmentSize(size)}
        </span>
      </span>
      {href ? (
        <span className="ml-auto h-5 opacity-0 group-hover:opacity-100">
          <DownloadSimple size={20} aria-hidden />
        </span>
      ) : null}
    </a>
  );
};

export function configureAttachmentNodeView(ctx: Ctx, nodeViewFactory: ReturnType<typeof useNodeViewFactory>) {
  ctx.update(prosePluginsCtx, (plugins) => [
    ...plugins,
    new Plugin({
      props: {
        nodeViews: {
          [ATTACHMENT_NODE_NAME]: nodeViewFactory({ component: AttachmentNodeView }),
        },
      },
    }),
  ]);
}
